import { createContext, JSX, useContext, useEffect } from "react";

import { useLocalStorage } from "~/hook/base/useLocalStorage.ts";

export enum Theme {
  Dark = "dark",
  Light = "light",
  System = "system",
}

interface ThemeContextType {
  setTheme: (theme: Theme) => void;
  theme: Theme;
}

const ThemeContext = createContext<null | ThemeContextType>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }: { children: JSX.Element }) => {
  const [theme, setTheme] = useLocalStorage<Theme>("theme", Theme.System);

  useEffect(() => {
    const root = window.document.documentElement;

    const applyTheme = () => {
      if (
        theme === Theme.Dark ||
        (theme === Theme.System &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    };

    applyTheme();

    if (theme === Theme.System) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const listener = () => {
        applyTheme();
      };
      mediaQuery.addEventListener("change", listener);
      return () => {
        mediaQuery.removeEventListener("change", listener);
      };
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ setTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};
